import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BoxDetailResponse, BoxListFilters, BoxListResponse } from '@/tms/box-list/_models/box-list.model';
import {
	GboxVehicleAssignment,
	GboxVehicleAssignmentApiData,
	GboxVehicleAssignmentPayload,
	VehicleSearchResult,
} from '@/tms/box-list/_models/box-vehicle-assignment.model';
import { snakeToCamelCaseObject } from '@/_utils/string/snake-to-camel-case';
import { buildHttpParams } from '@/common/utils/http-params.util';

@Injectable({ providedIn: 'root' })
export class BoxListApiService {
	private readonly http = inject(HttpClient);
	private readonly baseUrl = `${environment.erpSymfonyUrl}/api/v1/tms/box-list`;
	private readonly symfonyUrl = environment.erpSymfonyUrl;

	list(page: number, limit: number, filters: BoxListFilters = {}): Observable<BoxListResponse> {
		const params = buildHttpParams({
			page,
			limit,
			boxId: filters.boxId,
			vehicleId: filters.vehicleId,
		});

		return this.http.get<BoxListResponse>(this.baseUrl, { params });
	}

	get(id: number, page: number, limit: number): Observable<BoxDetailResponse> {
		const params = new HttpParams().set('page', page).set('limit', limit);

		return this.http.get<BoxDetailResponse>(`${this.baseUrl}/${id}`, { params });
	}

	sync(): Observable<BoxListResponse> {
		return this.http.post<BoxListResponse>(`${this.baseUrl}/sync`, {});
	}

	syncAll(): Observable<BoxListResponse> {
		return this.http.post<BoxListResponse>(`${this.baseUrl}/sync-all`, {});
	}

	getScanContent(boxId: number, scanId: number): Observable<Blob> {
		return this.http.get(`${this.baseUrl}/${boxId}/scans/${scanId}/content`, {
			responseType: 'blob',
		});
	}

	listAssignments(boxId: number): Observable<GboxVehicleAssignment[]> {
		return this.http
			.get<{ data: GboxVehicleAssignmentApiData[] }>(`${this.baseUrl}/${boxId}/vehicle-assignments`)
			.pipe(map((r) => r.data.map((item) => snakeToCamelCaseObject(item))));
	}

	createAssignment(boxId: number, payload: GboxVehicleAssignmentPayload): Observable<GboxVehicleAssignment> {
		return this.http
			.post<{ data: GboxVehicleAssignmentApiData }>(`${this.baseUrl}/${boxId}/vehicle-assignments`, payload)
			.pipe(map((r) => snakeToCamelCaseObject(r.data)));
	}

	updateAssignment(
		boxId: number,
		id: number,
		payload: GboxVehicleAssignmentPayload,
	): Observable<GboxVehicleAssignment> {
		return this.http
			.put<{ data: GboxVehicleAssignmentApiData }>(`${this.baseUrl}/${boxId}/vehicle-assignments/${id}`, payload)
			.pipe(map((r) => snakeToCamelCaseObject(r.data)));
	}

	deleteAssignment(boxId: number, id: number): Observable<void> {
		return this.http.delete<void>(`${this.baseUrl}/${boxId}/vehicle-assignments/${id}`);
	}

	searchVehicles(query: string): Observable<VehicleSearchResult[]> {
		return this.http.get<VehicleSearchResult[]>(`${this.symfonyUrl}/vehicles/search`, {
			params: buildHttpParams({ by: query }),
		});
	}
}
